// src/components/DataTable.js
import React, { useMemo, useState, useEffect } from 'react';
import Dropdown from './Dropdown';
import ContextMenu from './ContextMenu';
import { jsonToCsv, downloadFile, DIM_FIELDS } from '../utils';

function summarize(allRows, filteredRows, dim, valueType){
  const metricMap = new Map();
  const valueField = 'Avg Value';

  const dataRows = filteredRows.filter(r => r['Value Type'] === valueType);

  for (const r of dataRows){
    let key = r[dim] ?? '—';
    // Group "Total" with null/undefined values
    if (String(key).toLowerCase() === 'total' || key === '—') {
      key = 'Total';
    }
    const v = Number(r[valueField]);
    if (!Number.isFinite(v)) continue;
    const cur = metricMap.get(key) || { sum:0, count:0 };
    cur.sum += v; cur.count += 1;
    metricMap.set(key, cur);
  }
  
  // Only include categories that have data for the current value type
  const allDimValues = Array.from(new Set(dataRows.map(r => {
    let key = r[dim] ?? '—';
    if (String(key).toLowerCase() === 'total' || key === '—') {
      key = 'Total';
    }
    return key;
  })));

  const out = allDimValues.map(key => {
    if (metricMap.has(key)) {
      const { sum, count } = metricMap.get(key);
      const value = sum / Math.max(count, 1);
      return { key, value };
    }
    return { key, value: null };
  });

  out.sort((a,b)=> (b.value ?? -Infinity) - (a.value ?? -Infinity));
  return out;
}

export default function DataTable({ rows, allRows, defaultDim='Input', filters, onFiltersChange }){
  const [dim, setDim] = useState(defaultDim);
  
  // Reset the dimension when the mineral filter changes, to respect defaultDim
  useEffect(() => {
    setDim(defaultDim);
  }, [filters.Mineral, defaultDim]);

  const data = useMemo(()=>summarize(allRows, rows, dim, filters.value_type), [allRows, rows, dim, filters.value_type]);
  const [contextMenu, setContextMenu] = useState(null);

  const handleRowClick = (value) => {
    if (!onFiltersChange || !filters || value === '—') return;

    if (dim === 'Year') {
      if (filters.yearMin === value && filters.yearMax === value) {
        onFiltersChange({ ...filters, yearMin: '', yearMax: '' });
      } else {
        onFiltersChange({ ...filters, yearMin: value, yearMax: value });
      }
      return;
    }
    
    const currentFilterValue = filters[dim];
    let newFilterValue;

    if (Array.isArray(currentFilterValue)) {
      if (currentFilterValue.length === 1 && currentFilterValue[0] === value) {
        newFilterValue = []; // Unfilter if clicking the same item
      } else {
        newFilterValue = [value]; // Filter by the new item
      }
    } else {
      if (currentFilterValue === value) {
        newFilterValue = ''; // Unfilter
      } else {
        newFilterValue = value; // Filter
      }
    }
    
    onFiltersChange({ ...filters, [dim]: newFilterValue });
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    if (!data?.length) return;

    const downloadCSV = () => {
        const csv = jsonToCsv(data);
        downloadFile(csv, 'table-data.csv', 'text/csv;charset=utf-8;');
    };

    const copyCSV = () => {
        const csv = jsonToCsv(data);
        navigator.clipboard.writeText(csv).catch(err => console.error('Failed to copy CSV: ', err));
    };

    setContextMenu({
        x: e.clientX, y: e.clientY,
        options: [
            { label: 'Download data as CSV', action: downloadCSV },
            { label: 'Copy data to clipboard', action: copyCSV },
        ]
    });
  };

  const unitSuffix = (filters.value_type === 'Cost' || filters.value_type === 'Price') ? ' USD/T' : ' T';

  return (
    <div className="panel table" onContextMenu={handleContextMenu}>
      <div className="panel-header">
        <div className="panel-title">
          <h3>Breakdown</h3>
        </div>
      </div>
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>
                <Dropdown value={dim} onChange={setDim} options={DIM_FIELDS.filter(f => f !== 'Mineral')} />
              </th>
              <th>
                <Dropdown 
                  value={filters.value_type} 
                  onChange={v => onFiltersChange({...filters, value_type: v})} 
                  options={['Cost', 'Price', 'Supply', 'Demand']} 
                  hideAll={true} 
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {data.slice(0, 250).map((r,i)=>{
              const filterValue = filters?.[dim];
              const isSelected = Array.isArray(filterValue) ? filterValue.includes(r.key) : filterValue === r.key;
              const isFiltered = Array.isArray(filterValue) ? filterValue.length > 0 : !!filterValue;

              return (
                <tr 
                  key={r.key + i}
                  onClick={() => handleRowClick(r.key)}
                  className={`${(isFiltered && !isSelected) ? 'muted' : ''} ${isSelected ? 'selected' : ''}`}
                  style={{ cursor: onFiltersChange && r.key !== '—' ? 'pointer' : 'default' }}
                >
                  <td>{r.key}</td>
                  <td>
                    {Number.isFinite(r.value) ? r.value.toLocaleString(undefined, {maximumFractionDigits:2}) : '—'}
                    {Number.isFinite(r.value) ? unitSuffix : ''}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {contextMenu && <ContextMenu {...contextMenu} onRequestClose={() => setContextMenu(null)} />}
    </div>
  );
}